import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import debounce from 'lodash.debounce';
import { useNavigate } from 'react-router-dom';

import useAllProjects from '../../hooks/useAllProjects';
import useFantomStarterAdminApi from '../../hooks/useFantomStarterAdminApi';
import { DesktopLayout } from '../../components/Layouts';
import { NoContentFound } from '../../components/NoContentFound';
import { SaleCard } from '../../components/Cards';
import { search, warning } from '../../assets';
import { extractNameAndTag } from '../../utils/conversion';
import useDocusignApi from '../../hooks/useDocusign';
import { NotificationCard } from '../../components/Cards';
import { useActiveAccount } from 'thirdweb/react';
import { RegisteredBankAccount } from '../../components/Settings/BankAccount';

const Sales = () => {
  const tabs = ['All', 'Funds', 'Syndicates', 'Bonds'];
  const { projects, refetch: refetchProjects } = useAllProjects();
  const {
    circle: { getWireBanks },
  } = useFantomStarterAdminApi();
  const { getTemplates } = useDocusignApi();
  const navigate = useNavigate();
  const activeAccount = useActiveAccount();

  const registeredBanks: RegisteredBankAccount[] = JSON.parse(
    localStorage.getItem(`${activeAccount?.address}-banks`) || '[]'
  );

  const [activeTab, setActiveTab] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [userWireIds, setUserWireIds] = useState<string[]>([]);
  const [templateHash, setTemplateHash] = useState<{
    [templateName: string]: any;
  }>({});

  const isAccountLinked = userWireIds.length > 0 || registeredBanks.length > 0;

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(e.target.value);
  };

  const debouncedSearch = useMemo(
    () => debounce((query: string) => setSearchQuery(query), 300),
    []
  );

  const filteredProjects = useMemo(() => {
    let filtered = projects;

    if (activeTab !== 'All') {
      filtered = projects.filter((project) => {
        const projectName = extractNameAndTag(project.name).name.toLowerCase();
        if (activeTab === 'Funds') {
          return projectName.includes('fund');
        } else if (activeTab === 'Syndicates') {
          return projectName.includes('syndicate');
        } else if (activeTab === 'Bonds') {
          return projectName.includes('bond');
        }
        return true;
      });
    }

    if (searchQuery) {
      const lowercasedQuery = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (project) =>
          project.name.toLowerCase().includes(lowercasedQuery) ||
          project.id.toLowerCase().includes(lowercasedQuery)
      );
    }

    return filtered;
  }, [activeTab, projects, searchQuery]);

  useEffect(() => {
    (async () => {
      const result = await getWireBanks();
      if (result.status === 200) {
        setUserWireIds(result.data || []);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useEffect(() => {
    getTemplates().then((result: any) => {
      if (!result || !result.envelopeTemplates) return;
      const templates: any[] = result.envelopeTemplates;
      const hash: { [templateName: string]: any } = {};
      templates.forEach((template) => (hash[template.name] = template));
      setTemplateHash(hash);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <DesktopLayout>
      <main className="pt-[92px] px-4 sm:px-8 lg:px-16 xl:px-32 flex flex-col gap-5 sm:gap-20 items-start">
        <section className="mt-6 sm:mt-16 flex flex-col w-full">
          {!isAccountLinked && (
            <div className="w-full mb-6 sm:mb-8">
              <NotificationCard
                iconSrc={warning}
                title="Complete your seller profile to accept payments"
                buttonText="Link bank account"
                classname="box-border w-full p-4"
                onButtonClick={() => navigate('/settings?tab=Bank%20Account')}
              />
            </div>
          )}
          <header className="flex flex-col sm:flex-row gap-4 w-full text-sm font-medium justify-between">
            <nav className="flex w-full sm:w-auto sm:min-w-[240px] text-slate-700 sm:max-w-[280px] overflow-x-auto">
              <div className="flex items-center p-1.5 w-full rounded-md bg-slate-100">
                {tabs.map((label) => (
                  <div
                    key={label}
                    className={`flex gap-2.5 px-3 py-1.5 rounded cursor-pointer whitespace-nowrap ${
                      activeTab === label ? 'bg-white text-slate-900' : ''
                    }`}
                    onClick={() => setActiveTab(label)}
                  >
                    <span>{label}</span>
                  </div>
                ))}
              </div>
            </nav>

            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
              <div className="flex items-center rounded-md border shadow-sm border-slate-200 bg-white px-2 py-2.5 gap-2 w-full sm:w-auto sm:min-w-[280px]">
                <img
                  loading="lazy"
                  src={search}
                  className="w-4"
                  alt="Search icon"
                />
                <input
                  type="search"
                  placeholder="Search for a sale"
                  className="flex-1 bg-transparent border-none outline-none"
                  aria-label="Search for a sale"
                  onChange={handleSearchChange}
                />
              </div>
              <Link to="/sales/create-fund" className="w-full sm:w-auto">
                <button className="btn-primary w-full sm:w-auto">Start a sale</button>
              </Link>
            </div>
          </header>
          {filteredProjects.length > 0 ? (
            <section className="mt-6 pb-4 grid gap-4 sm:gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {filteredProjects.map((project) => (
                <SaleCard
                  key={project.id}
                  project={project}
                  templateHash={templateHash}
                  refetchProjects={refetchProjects}
                />
              ))}
            </section>
          ) : (
            <NoContentFound />
          )}
        </section>
      </main>
    </DesktopLayout>
  );
};

export default Sales;
